import { supabase } from "~/integrations/supabase/client";
import { SalesRequest } from "~/types/client";

/**
 * Transform a database record from sales_request table to frontend SalesRequest type
 * This handles the snake_case to camelCase conversion and proper type assertions
 */
export function transformDbToSalesRequest(dbRecord: any): SalesRequest {
  return {
    id: dbRecord.id,
    clientId: dbRecord.client_id,
    createdBy: dbRecord.created_by,
    approvedRejectedBy: dbRecord.approved_rejected_by || undefined,
    assigned_transportista: dbRecord.assigned_transportista || undefined,
    status: dbRecord.status as SalesRequest["status"],
    paid: dbRecord.paid,
    paid_amount: dbRecord.paid_amount || undefined,
    payment_count: dbRecord.payment_count || undefined,
    paymentTerms: dbRecord.payment_terms,
    paymentType: dbRecord.payment_type as "future" | "prepaid",
    paymentDueDate: dbRecord.payment_due_date,
    totalAmount: dbRecord.total_amount,
    price_with_iva: dbRecord.price_with_iva || undefined,
    isExento: dbRecord.is_exento || undefined,
    isNonSale: dbRecord.is_non_sale || undefined,
    hasPricingWarning: dbRecord.has_pricing_warning || undefined,
    comments: dbRecord.comments || undefined,
    internal_notes: dbRecord.internal_notes || undefined,
    weight_kg: dbRecord.weight_kg || undefined,
    volume_m3: dbRecord.volume_m3 || undefined,
    factura_observations: dbRecord.factura_observations || undefined,
    factura_creation_date: dbRecord.factura_creation_date || undefined,
    deliveryDate: dbRecord.delivery_date || undefined,
    requestedDeliveryDateStart:
      dbRecord.requested_delivery_date_start || undefined,
    requestedDeliveryDateEnd: dbRecord.requested_delivery_date_end || undefined,
    effectiveDeliveryDateStart:
      dbRecord.effective_delivery_date_start || undefined,
    effectiveDeliveryDateEnd: dbRecord.effective_delivery_date_end || undefined,
    deliveryAddressId: dbRecord.delivery_address_id || undefined,
    deliveryStreet: dbRecord.delivery_street || undefined,
    deliveryNumber: dbRecord.delivery_number || undefined,
    deliveryComplement: dbRecord.delivery_complement || undefined,
    deliveryCity: dbRecord.delivery_city || undefined,
    deliveryState: dbRecord.delivery_state || undefined,
    deliveryComuna: dbRecord.delivery_comuna || undefined,
    rejectedReason: dbRecord.rejected_reason || undefined,
    previousStatus: dbRecord.previous_status || undefined,
    createdAt: dbRecord.created_at,
    updatedAt: dbRecord.updated_at,
    salesConditionId: dbRecord.sales_condition_id || undefined,
    creation_lat: dbRecord.creation_lat || undefined,
    creation_lng: dbRecord.creation_lng || undefined,
    requiresPrepayment: dbRecord.requires_prepayment || undefined,
    telecheque_id: dbRecord.telecheque_id || undefined,
    deleted_at: dbRecord.deleted_at || undefined,
    factura_number: dbRecord.factura_number || undefined,
    invoice_printed_at: dbRecord.invoice_printed_at || undefined,
  };
}

/**
 * Fetch a sales request by ID and transform it to frontend format
 * Returns null if not found or on error
 */
export async function fetchAndTransformSalesRequest(
  salesRequestId: string
): Promise<SalesRequest | null> {
  try {
    const { data, error } = await supabase
      .from("sales_request")
      .select("*")
      .eq("id", salesRequestId)
      .single();

    if (error) {
      console.error("Error fetching sales request:", error);
      return null;
    }

    if (!data) return null;

    return transformDbToSalesRequest(data);
  } catch (error) {
    console.error("Error in fetchAndTransformSalesRequest:", error);
    return null;
  }
}

/**
 * Helper to refresh a sales request and update state
 * Commonly used pattern after updating a sales request
 */
export async function refreshSalesRequestState(
  salesRequestId: string,
  setSelectedOrder: (order: SalesRequest | null) => void,
  onOrderStatusChange?: () => void
): Promise<boolean> {
  const updatedOrder = await fetchAndTransformSalesRequest(salesRequestId);

  if (updatedOrder) {
    setSelectedOrder(updatedOrder);
    if (onOrderStatusChange) {
      onOrderStatusChange();
    }
    return true;
  }

  return false;
}
